import React from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { catColor, abbrev } from "@/planner/lib/vendy";
import { productLabel } from "@/planner/lib/format";
import { shelfPrice, money } from "@/planner/lib/pricing";
import { fileUrl } from "@/planner/lib/apiClient";

const BOTTLES = ["Soda", "Water", "Sports", "Juice", "Tea"];

// Simple product silhouettes so the shelves read like a real cooler: bottles, cans, bags, bars.
export const ProductArt = ({ category, className = "" }) => {
  const c = catColor(category);
  const fill = c.stripe;
  const common = { className, viewBox: "0 0 40 80", preserveAspectRatio: "xMidYMax meet", "aria-hidden": true };
  if (BOTTLES.includes(category)) {
    return (
      <svg {...common}>
        <rect x="15" y="2" width="10" height="7" rx="1.5" fill="#111827" opacity="0.85" />
        <path d="M16 9 h8 v6 c0 4 8 7 8 14 v44 a5 5 0 0 1 -5 5 h-14 a5 5 0 0 1 -5 -5 v-44 c0 -7 8 -10 8 -14 z" fill={fill} opacity={category === "Water" ? 0.55 : 0.92} />
        <rect x="8" y="40" width="24" height="16" fill="#fff" opacity="0.85" />
        <rect x="11" y="18" width="3" height="50" rx="1.5" fill="#fff" opacity="0.3" />
      </svg>
    );
  }
  if (category === "Energy") {
    return (
      <svg {...common}>
        <rect x="9" y="22" width="22" height="56" rx="4" fill={fill} />
        <rect x="10" y="20" width="20" height="4" rx="2" fill="#9CA3AF" />
        <rect x="9" y="42" width="22" height="12" fill="#111827" opacity="0.55" />
        <rect x="12" y="26" width="3" height="48" rx="1.5" fill="#fff" opacity="0.3" />
      </svg>
    );
  }
  if (category === "Chips") {
    return (
      <svg {...common}>
        <path d="M5 26 l3 -3 l3 3 l3 -3 l3 3 l3 -3 l3 3 l3 -3 l3 3 l3 -3 l3 3 l2 -2 c1 18 1 34 0 50 l-2 2 l-3 -3 l-3 3 l-3 -3 l-3 3 l-3 -3 l-3 3 l-3 -3 l-3 3 l-3 -3 l-3 3 c-1 -18 -1 -34 0 -52 z" fill={fill} />
        <ellipse cx="20" cy="50" rx="10" ry="8" fill="#FBBF24" opacity="0.9" />
        <rect x="9" y="30" width="22" height="6" rx="2" fill="#fff" opacity="0.85" />
      </svg>
    );
  }
  if (category === "Candy") {
    return (
      <svg {...common}>
        <path d="M8 38 l2 -2 l2 2 l2 -2 l2 2 l2 -2 l2 2 l2 -2 l2 2 l2 -2 l2 2 l2 -2 l2 2 v38 l-2 2 l-2 -2 l-2 2 l-2 -2 l-2 2 l-2 -2 l-2 2 l-2 -2 l-2 2 l-2 -2 l-2 2 l-2 -2 z" fill={fill} />
        <rect x="11" y="50" width="18" height="10" rx="2" fill="#fff" opacity="0.85" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <rect x="7" y="34" width="26" height="44" rx="3" fill={fill} />
      <rect x="10" y="46" width="20" height="10" rx="2" fill="#fff" opacity="0.85" />
    </svg>
  );
};

// One facing on a shelf: drop target, and drag source when it holds a product.
const Facing = ({ s, product, dndEnabled, selected, picking, onClick, tall }) => {
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `slot:${s.slot_id}`, disabled: !dndEnabled });
  const { setNodeRef: setDragRef, listeners, attributes, isDragging } = useDraggable({ id: `slotsrc:${s.slot_id}`, disabled: !dndEnabled || !product });
  const cur = s.current ?? 0;
  const par = s.par || 0;
  const low = product && par > 0 && cur < par;
  return (
    <div ref={setDropRef} className="min-w-0 flex-1" style={{ opacity: isDragging ? 0.35 : undefined }}>
      <button
        type="button"
        ref={setDragRef}
        {...listeners}
        {...attributes}
        onClick={onClick}
        data-testid={`cooler-slot-${s.slot_id}`}
        title={product ? `${s.slot_id} · ${productLabel(product)}` : `${s.slot_id} · empty`}
        className={`relative flex w-full touch-manipulation flex-col items-center justify-end rounded-md px-0.5 pt-1 transition-colors ${tall ? "h-[88px] sm:h-[112px]" : "h-[70px] sm:h-[88px]"} ${
          isOver ? "bg-sky-300/50 ring-2 ring-sky-500" : selected ? "bg-white/50 ring-2 ring-primary" : picking ? "hover:bg-sky-200/60 ring-1 ring-sky-400/60" : "hover:bg-white/30"
        }`}
      >
        {product ? (
          <>
            {fileUrl(product.image_path)
              ? <img src={fileUrl(product.image_path)} alt={productLabel(product)} loading="lazy" draggable={false} className="min-h-0 w-full flex-1 object-contain object-bottom drop-shadow" data-testid={`cooler-photo-${s.slot_id}`} />
              : <ProductArt category={product.category || "Other"} className="h-full w-full max-w-[44px]" />}
            <span className="mt-0.5 hidden w-full truncate text-center text-[9px] font-semibold leading-tight text-slate-800 sm:block">{abbrev(product.name, 12)}</span>
            {par > 0 && (
              <span className={`absolute right-0 top-0 rounded px-1 font-mono text-[9px] font-bold leading-4 ${low ? (cur === 0 ? "bg-red-600 text-white" : "bg-amber-500 text-white") : "bg-white/80 text-slate-700"}`}>
                {cur}/{par}
              </span>
            )}
          </>
        ) : (
          <span className="flex h-full w-full items-center justify-center rounded border border-dashed border-slate-400/70 text-lg font-light text-slate-400">+</span>
        )}
      </button>
    </div>
  );
};

const isDrinkRow = (slots, pmap, label) => {
  if (/bev|drink|soda|water|energy|juice|tea|sport/i.test(label || "")) return true;
  const cats = slots.map((s) => pmap[s.product_id]?.category).filter(Boolean);
  return cats.length > 0 && cats.filter((c) => BOTTLES.includes(c) || c === "Energy").length >= cats.length / 2;
};

// A picture of the cooler: cabinet, glass door, shelves with price tags under each facing.
export default function CoolerView({ title, rowGroups, pmap, rowLabel, dndEnabled, selectedId, picking, globalMargin, onSlotClick }) {
  return (
    <div className="mx-auto w-full max-w-[640px]" data-testid="cooler-view">
      <div className="rounded-[18px] bg-gradient-to-b from-slate-800 to-slate-900 p-2 shadow-xl sm:p-3">
        <div className="mb-2 flex items-center justify-between rounded-md bg-slate-950 px-3 py-1.5">
          <span className="truncate text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-200">{title}</span>
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            <span className="h-1.5 w-6 rounded-full bg-sky-400/70" />
          </span>
        </div>
        <div className="relative flex gap-1.5 sm:gap-2">
          <div className="relative flex-1 overflow-hidden rounded-lg border-2 border-slate-600 bg-gradient-to-br from-sky-50 via-white to-sky-100 px-1.5 py-2 sm:px-3">
            <div className="pointer-events-none absolute -left-10 top-0 h-full w-24 rotate-12 bg-white/40 blur-md" />
            <div className="relative space-y-2">
              {rowGroups.map(([letter, slots]) => {
                const label = rowLabel(letter);
                const tall = isDrinkRow(slots, pmap, label);
                return (
                  <div key={letter} data-testid={`cooler-row-${letter}`}>
                    <div className="flex items-end gap-0.5 sm:gap-1">
                      {slots.map((s) => (
                        <Facing
                          key={s.slot_id}
                          s={s}
                          product={s.product_id ? pmap[s.product_id] : null}
                          dndEnabled={dndEnabled}
                          selected={selectedId === s.slot_id}
                          picking={picking}
                          tall={tall}
                          onClick={() => onSlotClick(s)}
                        />
                      ))}
                    </div>
                    <div className="h-1.5 rounded-sm bg-gradient-to-b from-slate-300 to-slate-500 shadow" />
                    <div className="flex gap-0.5 sm:gap-1">
                      {slots.map((s) => {
                        const p = s.product_id ? pmap[s.product_id] : null;
                        const price = p ? shelfPrice(p, globalMargin) : null;
                        return (
                          <div key={s.slot_id} className="min-w-0 flex-1 text-center" data-testid={`cooler-tag-${s.slot_id}`}>
                            <div className={`mx-auto mt-0.5 truncate rounded-sm px-0.5 font-mono text-[8px] leading-[14px] sm:text-[10px] ${p ? "bg-yellow-200 font-bold text-slate-900" : "text-slate-400"}`}>
                              {p ? (price != null ? money(price).replace(".00", "") : "$?") : s.slot_id}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {label && <div className="mt-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-500">{letter} · {label}</div>}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex w-2 shrink-0 items-center sm:w-3">
            <div className="h-1/2 w-full rounded-full bg-gradient-to-r from-slate-400 to-slate-200" />
          </div>
        </div>
        <div className="mt-2 h-3 rounded-b-md bg-slate-950" />
      </div>
    </div>
  );
}
